import {
  AuthStore,
  CommonsStore,
  LoggerFactory,
  RemoteApi,
  User
} from "lego-gui-lib";
import { action, reaction } from "mobx";
import { ParsedQuery } from "query-string";
import { IssuerService, LoginResponse, SSO } from "./IssuerService";
import { InternalSSOService } from "./InternalSSOService";
import { ExternalSSOService } from "./ExternalSSOService";
import { getBaseUrl } from "../../common/common";
import { remove } from "lodash";

const log = LoggerFactory.create("auth");

enum Roles {
  CUSTOMER = "CUSTOMER",
  CUSTOMER_READ_ONLY = "CUSTOMER_READ_ONLY",
  /** Customer Owner is a special customer supervisor role, with option to see application as a Customer of choice.
   * This role is simulated for frontend mechanisms to work properly, it is NOT retrieved from backend */
  CUSTOMER_OWNER = "CUSTOMER_OWNER",
  /** Customer HG is not used anywhere from business standpoint for now, kept ready on purpose */
  CUSTOMER_HG = "CUSTOMER_HG",
  ADMIN = "ADMIN",
  /** Platform Admin is an additional role, allows access to Properties view */
  PLATFORM_ADMIN = "PLATFORM_ADMIN",
  /** SAN Admin is customer supervisor role, with option to see application as a Customer of choice */
  SAN_ADMIN = "SAN_ADMIN",
  SAN_ADMIN_READ_ONLY = "SAN_ADMIN_READ_ONLY"
}

export interface RedirectState {
  nextHop: string;
  issuer: string;
}

export interface MyCustomer {
  customerId: string;
  role: string;
}

export interface HnsUser extends User {
  customerExternalId: string;
  customerId: string;
  myCustomers: MyCustomer[];
}

export class Auth extends AuthStore {
  public static Roles = {
    ANYONE: [
      Roles.ADMIN,
      Roles.CUSTOMER,
      Roles.CUSTOMER_READ_ONLY,
      Roles.CUSTOMER_HG,
      Roles.SAN_ADMIN,
      Roles.SAN_ADMIN_READ_ONLY,
      Roles.CUSTOMER_OWNER
    ],
    ANYONE_WITH_WRITE: [Roles.ADMIN, Roles.CUSTOMER, Roles.SAN_ADMIN],
    ANY_ADMIN: [Roles.ADMIN],
    ANY_READONLY: [Roles.CUSTOMER_READ_ONLY, Roles.SAN_ADMIN_READ_ONLY],
    ANY_SAN_ADMIN: [Roles.SAN_ADMIN, Roles.SAN_ADMIN_READ_ONLY],
    ANY_CUSTOMER_OWNER: [
      Roles.SAN_ADMIN,
      Roles.SAN_ADMIN_READ_ONLY,
      Roles.CUSTOMER_OWNER
    ],
    ANY_CUSTOMER: [
      Roles.CUSTOMER,
      Roles.CUSTOMER_READ_ONLY,
      Roles.CUSTOMER_HG,
      Roles.CUSTOMER_OWNER
    ],
    ...Roles
  };
  public static COOKIE_NAME = "cookie_name";

  public landingPage = "";

  private remoteApi: RemoteApi;
  private issuers: {
    [name: string]: IssuerService;
  };

  public constructor(api: RemoteApi, commons: CommonsStore) {
    super(api, commons);
    this.remoteApi = api;
    this.issuers = {
      InternalSSO: new InternalSSOService(api),
      ExternalSSO: new ExternalSSOService(api)
    };

    reaction(
      () => this.currentUser as HnsUser,
      currentUser => {
        if (currentUser) {
          /** This will only work for Customer who has myCustomers assigned */
          this.assignCustomerOwnerRole();

          commons.trigger("auth.profile", [currentUser]);
          this.setUsername(currentUser);
          this.setLandingPage();
        }
      }
    );

    this.remoteApi.addErrorInterceptor(this.remoteErrorHandler);
  }

  public isAdmin(): boolean {
    return this.checkRoles(role => role === Roles.ADMIN);
  }

  public isPlatformAdmin(): boolean {
    return this.checkRoles(role => role === Roles.PLATFORM_ADMIN);
  }

  public isReadOnly(): boolean {
    return this.checkRoles(role =>
      Auth.Roles.ANY_READONLY.includes(role as Roles)
    );
  }

  public isSanAdmin(): boolean {
    return this.checkRoles(role =>
      Auth.Roles.ANY_SAN_ADMIN.includes(role as Roles)
    );
  }

  public isCustomer(): boolean {
    return this.checkRoles(role =>
      Auth.Roles.ANY_CUSTOMER.includes(role as Roles)
    );
  }

  public isCustomerOwner(): boolean {
    return this.checkRoles(role => role === Auth.Roles.CUSTOMER_OWNER);
  }

  public isAnyCustomerOwner(): boolean {
    return this.checkRoles(role =>
      Auth.Roles.ANY_CUSTOMER_OWNER.includes(role as Roles)
    );
  }

  private checkRoles(condition: (role: string) => boolean): boolean {
    return this.getRoles().some(condition);
  }

  private getRoles(): string[] {
    if (!this.currentUser) {
      return [];
    }
    return this.currentUser.roles;
  }

  /** Check if Customer has any myCustomers assigned */
  @action
  private assignCustomerOwnerRole(): void {
    if (this.currentUser) {
      const user = this.currentUser as HnsUser;
      if (user.myCustomers && user.myCustomers?.length > 1) {
        this.currentUser.roles.push(Auth.Roles.CUSTOMER_OWNER);
      }
    }
  }

  /** Used for Customer Owner myCustomers - allows to check role for assigned Customer */
  @action
  public assignCustomerRole(customerRef: string): void {
    const user = this.currentUser as HnsUser;
    const myCustomer = user.myCustomers?.find(
      customer => customer.customerId.toString() === customerRef.toString()
    );
    if (myCustomer) {
      const counterRole =
        myCustomer.role === Roles.CUSTOMER_READ_ONLY
          ? Roles.CUSTOMER
          : Roles.CUSTOMER_READ_ONLY;
      remove(user.roles, role => role === counterRole);
      if (!user.roles.includes(myCustomer.role)) {
        user.roles.push(myCustomer.role);
      }
      log.debug("Selected Customer roles:", user.roles);
    }
  }

  @action
  public setUsername(currentUser: HnsUser): void {
    const externalIdIfCustomer =
      currentUser.customerExternalId && !this.isCustomerOwner()
        ? `[${currentUser.customerExternalId}]`
        : "";
    this.username = `${currentUser.username} ${externalIdIfCustomer}`;
  }

  @action
  public async redirectToSSO(): Promise<void> {
    const sso = await this.remoteApi.getMethod<SSO>("/public/auth/sso");
    log.debug("SSO info", sso);
    Auth.setCookieName(sso.cookieName);
    this.persisteStateBeforeRedirect(sso);
    await this.issuers[sso.name].redirect(sso);
  }

  @action
  public setLandingPage(): void {
    if (this.isAnyCustomerOwner()) {
      this.landingPage = "/customers";
    } else if (this.isPlatformAdmin() && !this.isAdmin()) {
      this.landingPage = "/properties";
    } else {
      this.landingPage = "/sans";
    }
  }

  @action
  public async redirectToSSOLogout(): Promise<void> {
    const sso = await this.remoteApi.getMethod<SSO>("/public/auth/sso");
    this.persisteStateBeforeRedirect(sso);
    await this.issuers[sso.name].logoutRedirect(sso);
  }

  @action
  public async loginWithAccessCode(
    params: ParsedQuery,
    redirectState: RedirectState
  ): Promise<LoginResponse> {
    const loginResponse = await this.issuers[redirectState.issuer].login(
      params,
      redirectState
    );

    this.remoteApi.setCsrfToken(loginResponse.csrf);

    return loginResponse;
  }

  private remoteErrorHandler = async (error: any) => {
    if (error.response.status === 401) {
      this.remoteApi.clearCsrfToken();
      const baseUrl = getBaseUrl();
      const isLoginView =
        window.location.href.indexOf(`${baseUrl}/loginRedirect`) !== -1;
      if (!isLoginView) {
        await this.redirectToSSO();
      } else {
        await this.redirectToSSOLogout();
        // TODO removed state and view if will be useless
        // window.location.href = `${baseUrl}/loginFailed`;
      }
    }

    return Promise.reject(error);
  };

  public static REDIRECT_STATE_NAME = "redirect_state";

  private persisteStateBeforeRedirect(sso: SSO) {
    const nextHop = window.location.href;
    const issuer = sso.name;

    window.sessionStorage.setItem(
      Auth.REDIRECT_STATE_NAME,
      JSON.stringify({ nextHop, issuer } as RedirectState)
    );
  }

  public getRedirectState(): RedirectState | null {
    const state = window.sessionStorage.getItem(Auth.REDIRECT_STATE_NAME);
    return state != null ? JSON.parse(state) : state;
  }

  private static setCookieName(cookieName: string) {
    window.sessionStorage.setItem(Auth.COOKIE_NAME, cookieName);
  }

  public getCookieName(): string {
    return window.sessionStorage.getItem(Auth.COOKIE_NAME) || "";
  }

  public async logout(): Promise<void> {
    await this.remoteApi.deleteMethod("/logout/sso").then(() => {
      this.remoteApi.clearCsrfToken();
      this.redirectToSSOLogout();
    });
  }
}
